import { Box, ColorOptions, IconNew as Icon, IconNew, ModalDialog, ModBox, Text } from '@jfs/ui-new'
import { useRoute } from '@react-navigation/native'
import * as React from 'react'
import { FC, useCallback, useEffect, useState } from 'react'
import { TouchableOpacity } from 'react-native'
import List from '../../../comps/List'
import { ListData } from '../../../comps/List/types'
import { ListItemType } from '../../../comps/ListItem'
import { NotificationV2 } from '../../../data/models/Notification'
import { staticStrings } from '../../../data/static/strings'
import { useStores } from '../../../data/store'
import { useWindowDimensions } from '../../../util/util'
import ProfileSettings from './ProfileSettings'
import SearchView from './SearchView'
import { styles } from './styles'

interface MenuProps {
  title?: string
  onProfilePress?: () => {}
  navigate: any
  path: string
  onSearchResultClicked: (show: boolean, txnId: string, txnDate?: string) => void
  handleNav: () => void
  hideSearchBar?: boolean
}

const renderCustomPortal = (
  listData: NotificationV2[],
  setAllNotificationsVisible: (isVisible: boolean) => void,
  setNotificationListVisible: (isVisible: boolean) => void,
  updateReadStatus: () => void,
  showViewAll: boolean,
  breakpoint: number,
): React.ReactElement => {
  const handleNotificationPress = (data: ListData): void => {
    setNotificationListVisible(false)
    setAllNotificationsVisible(false)
    window.open(data.url, staticStrings.targetBlank)
  }

  const handleViewAllClick = (): void => {
    setAllNotificationsVisible(true)
    setNotificationListVisible(false)
    updateReadStatus()
  }
  const themedStyle = styles(breakpoint)
  return (
    <Box {...themedStyle.notificationlistContainer}>
      <Box p={8}>
        <Text type={'h1'} hide={showViewAll}>
          {'Notifications'}
        </Text>
      </Box>
      <List type={ListItemType.NOTIFICATION} data={listData} selectable={false} onPress={handleNotificationPress} />
      <Box
        underlayColor="TRANSPARENT"
        testID="handleViewAll"
        onPress={handleViewAllClick}
        height={40}
        width={320}
        paddingVertical={12}>
        <Text hide={!showViewAll} {...themedStyle.viewAllText}>
          {'View All'}
        </Text>
      </Box>
    </Box>
  )
}

const renderModal = (
  listData: NotificationV2[],
  setAllNotificationsVisible: (isVisible: boolean) => void,
  setNotificationListVisible: (isVisible: boolean) => void,
  updateReadStatus: () => void,
  visible: boolean,
  showViewAll: boolean,
  breakpoint: number,
): React.ReactElement => {
  const handleModalPress = (): void => {
    setAllNotificationsVisible(false)
    setNotificationListVisible(false)
  }
  return (
    <ModalDialog
      children={renderCustomPortal(
        listData,
        setAllNotificationsVisible,
        setNotificationListVisible,
        updateReadStatus,
        showViewAll,
        breakpoint,
      )}
      show={visible}
      onDismiss={handleModalPress}
      type={'Bottom'}
    />
  )
}

const Menu: FC<MenuProps> = ({ path, handleNav, onSearchResultClicked, hideSearchBar = false }: MenuProps) => {
  const setTxnDetailsVisibility = useCallback(
    (value, txnId, txnDate) => onSearchResultClicked(value, txnId, txnDate),
    [onSearchResultClicked],
  )

  const handleSearchResultClick = (show: boolean, txnId: string, txnDate?: string) => {
    setTxnDetailsVisibility(show, txnId, txnDate)
  }
  const width = useWindowDimensions()
  const [notificationListVisible, setNotificationListVisible] = useState(false)
  const themedStyle = styles(width)
  const [allNotificationsVisible, setAllNotificationsVisible] = useState(false)

  const {
    notificationsData,
    count,
    selectedMname,
    displayCount,
    updateReadStatus,
    appTokens,
    syncTokens,
    isSearchBarVisible,
    hasSupersetPermission,
  } = useStores((store) => ({
    notificationsData: store?.user?.getNotifications(),
    count: store?.user?.getNotificationCount(),
    selectedMname: store?.settings?.metaData?.selectedMercName,
    displayCount: store?.user?.getDisplayCount(),
    updateReadStatus: store?.user?.updateReadStatus,
    appTokens: store.user.session.appIdToken,
    syncTokens: store.user.session.syncTokens,
    isSearchBarVisible: store.userManagement.isSearchBarVisible(),
    hasSupersetPermission: store?.userManagement?.hasSupersetPermission(),
  }))

  useEffect(() => {
    if (appTokens === '' && syncTokens) {
      syncTokens()
    }
  }, [appTokens, syncTokens])

  const onNotificationClick = (): void => {
    setNotificationListVisible(!notificationListVisible)
  }

  const listData = notificationsData?.filter((item: any) => item.isRead === false)
  const routes = useRoute()

  return (
    <Box {...themedStyle.menuContainer()}>
      {renderModal(
        listData,
        setAllNotificationsVisible,
        setNotificationListVisible,
        updateReadStatus,
        notificationListVisible,
        true,
        width,
      )}
      {renderModal(
        notificationsData,
        setAllNotificationsVisible,
        setNotificationListVisible,
        updateReadStatus,
        allNotificationsVisible,
        false,
        width,
      )}
      <Box
        {...themedStyle.notificationContainer}
        underlayColor="TRANSPARENT"
        testID="notification-view"
        onPress={onNotificationClick}>
        <>
          <Icon id={'NOTIFICATION'} color={'BLACK_21'} size={14} />
          <Box bg="ERROR" position="absolute" borderRadius={14} flex={1} height={14} width={14} right={-4} top={-4}>
            <Text hide={!(count > 0)} {...themedStyle.countStyle}>
              {displayCount}
            </Text>
          </Box>
        </>
      </Box>
      <Box flexDirection={'row'} alignItems={'flex-end'}>
        <ModBox
          show={routes.name === 'analyticalDashboard'}
          width={28}
          height={28}
          borderRadius={4}
          justifyContent={'center'}
          alignItems={'center'}
          marginRight={16}
          pb={2}
          onPress={() => {
            const element = document.getElementById('fullscreen-container')
            if (document?.fullscreenElement) {
              document.exitFullscreen()
            } else {
              if (element?.requestFullscreen) {
                element?.requestFullscreen()
              } else if (element?.mozRequestFullScreen) {
                // Firefox
                element?.mozRequestFullScreen()
              } else if (element?.webkitRequestFullscreen) {
                // Chrome, Safari, and Opera
                element?.webkitRequestFullscreen()
              } else if (element?.msRequestFullscreen) {
                // IE/Edge
                element?.msRequestFullscreen()
              }
            }
          }}
          borderColor={'#156378' as ColorOptions}
          underlayColor={'#79CADE' as ColorOptions}
          borderWidth={1}>
          <IconNew id="FULLSCREEN" color={'#156378' as ColorOptions} size={24} />
        </ModBox>
        <ProfileSettings mercName={selectedMname} />
      </Box>
      <ModBox show={isSearchBarVisible} {...themedStyle.searchViewContainer}>
        <TouchableOpacity onPress={handleNav} testID="notification-nav" style={themedStyle.bars}>
          <Icon id={'BARS'} />
        </TouchableOpacity>
        {!(hideSearchBar || hasSupersetPermission) && (
          <SearchView path={path} onSearchResultClicked={handleSearchResultClick} />
        )}
      </ModBox>
    </Box>
  )
}

export default Menu
